import requestConfig from 'Models/uiConfig/requestConfig';
import getAnalytics from 'Modules/analytics';
import { generateSearchId } from 'Modules/serverApi/common/searchId';
import type { SearchRequest, TrackEventTypeName } from 'Modules/serverApi/types';
import {
  hasOptionalBooleanField,
  hasOptionalLiteralField,
  hasOptionalStringField,
  hasStringField,
  isObjectRecord,
  isTruthy,
} from 'Utils/guards';
import { getSearchId } from 'Utils/userStorage';

import { sendBeacon } from './common/httpClient';
import { TRACK_ENDPOINT } from './endpoints';

const analytics = getAnalytics();
type ImpressionPayload = {
  Product: { Id: string; Position: number; PageNumber?: number; PageSize?: number };
};
const productImpressionQueue: ImpressionPayload[] = [];
const productImpressionBatchMs = 5000;
let productImpressionTimer: ReturnType<typeof setTimeout> | null = null;
let isFirstImpressionBatch = true;
const productImpressionKeys = new Set();
const maxImpressionKeys = 5000;
let lastSearchRequestKey = '';

type TelemetryScalar = string | number | boolean;
type TelemetryValue = TelemetryScalar | readonly TelemetryScalar[] | undefined;
type TelemetryAttributes = {
  [key: string]: TelemetryValue;
};
type ProductTrackingItem = {
  id?: string | number;
  type?: string;
};
type SearchRequestPagination = Pick<
  SearchRequest,
  | 'catalog'
  | 'filterQuery'
  | 'hiddenQuery'
  | 'mode'
  | 'pageNumber'
  | 'pageSize'
  | 'query'
  | 'selection'
  | 'sort'
  | 'variantsMode'
>;
type ProductImpressionEventParams = {
  item: ProductTrackingItem;
  position: number;
  searchRequest?: SearchRequestPagination;
};
type ProductClickEventParams = {
  item: ProductTrackingItem;
  position: number;
  searchTotalHits?: number;
};

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isOptionalFiniteNumber(value: unknown): value is number | undefined {
  return value == null || isFiniteNumber(value);
}

function isTelemetryScalar(value: unknown): value is TelemetryScalar {
  return typeof value === 'string' || typeof value === 'boolean' || isFiniteNumber(value);
}

function isTelemetryValue(value: unknown): value is TelemetryValue {
  return (
    value == null || isTelemetryScalar(value) || (Array.isArray(value) && value.every(isTelemetryScalar))
  );
}

function isTelemetryAttributes(value: unknown): value is TelemetryAttributes {
  return isObjectRecord(value) && Object.values(value).every(isTelemetryValue);
}

function isProductTrackingItem(value: unknown): value is ProductTrackingItem {
  if (!isObjectRecord(value)) {
    return false;
  }
  const { id, type } = value;
  return (id == null || isProductId(id)) && (type == null || typeof type === 'string');
}

const SELECTION_OPS = ['OR', 'NOT'] as const;

function isRequestSelectionItem(
  value: unknown,
): value is NonNullable<SearchRequestPagination['selection']>[number] {
  return (
    isObjectRecord(value) &&
    hasStringField(value, 'field') &&
    hasOptionalStringField(value, 'term') &&
    hasOptionalBooleanField(value, 'hidden') &&
    hasOptionalBooleanField(value, 'expand') &&
    hasOptionalLiteralField(value, 'op', SELECTION_OPS)
  );
}

function isRequestSelection(value: unknown): value is SearchRequestPagination['selection'] {
  return value == null || (Array.isArray(value) && value.every(isRequestSelectionItem));
}

function isSearchRequestPagination(value: unknown): value is SearchRequestPagination {
  return (
    value == null ||
    (isObjectRecord(value) &&
      hasOptionalStringField(value, 'catalog') &&
      hasOptionalStringField(value, 'filterQuery') &&
      hasOptionalStringField(value, 'hiddenQuery') &&
      hasOptionalStringField(value, 'mode') &&
      isOptionalFiniteNumber(value.pageNumber) &&
      isOptionalFiniteNumber(value.pageSize) &&
      hasOptionalStringField(value, 'query') &&
      isRequestSelection(value.selection) &&
      hasOptionalStringField(value, 'sort') &&
      hasOptionalStringField(value, 'variantsMode'))
  );
}

function isProductImpressionEventParams(value: unknown): value is ProductImpressionEventParams {
  return (
    isObjectRecord(value) &&
    isProductTrackingItem(value.item) &&
    isFiniteNumber(value.position) &&
    isSearchRequestPagination(value.searchRequest)
  );
}

function isProductClickEventParams(value: unknown): value is ProductClickEventParams {
  return (
    isObjectRecord(value) &&
    isProductTrackingItem(value.item) &&
    isFiniteNumber(value.position) &&
    isOptionalFiniteNumber(value.searchTotalHits)
  );
}

function isProductId(value: unknown): value is string | number {
  return typeof value === 'string' || isFiniteNumber(value);
}

function formatFacetValue(value: string): string {
  const escapedCharsReg = /[ /&:?\\%]/g;
  const startsWithDash = value.startsWith('-');
  return `${startsWithDash ? '-' : ''}${value.replace(/-/g, ' ').split(escapedCharsReg).filter(isTruthy).join('-')}`;
}

const getVehicleFacets = (): string[] | null => {
  try {
    const vehicle = window.Convermax.getVehicle();

    if (vehicle && typeof vehicle === 'object') {
      const facets = Object.entries(vehicle)
        .filter(([field, value]) => field && value)
        .map(([field, value]) => `${field}:${formatFacetValue(value)}`);

      if (facets.length > 0) {
        return facets;
      }
    }
  } catch {}

  return null;
};

const baseTrack = (type: TrackEventTypeName, eventParams: TelemetryAttributes): void => {
  const bodyFormData = new URLSearchParams();
  bodyFormData.append('UserID', analytics.userId);
  bodyFormData.append('SessionID', analytics.sessionId);
  bodyFormData.append('EventType', type);

  const searchId = getSearchId();
  if (searchId && !bodyFormData.has('SearchId')) {
    bodyFormData.append('SearchId', searchId);
  }

  const vehicleFacets = getVehicleFacets();
  if (vehicleFacets && vehicleFacets.length > 0) {
    vehicleFacets.forEach((facet) => bodyFormData.append('Facet', facet));
  }

  for (const eventParam in eventParams) {
    const value = eventParams[eventParam];
    if (Array.isArray(value)) {
      value.forEach((item) => bodyFormData.append(eventParam, String(item)));
    } else {
      bodyFormData.append(eventParam, String(value));
    }
  }

  try {
    sendBeacon(TRACK_ENDPOINT, bodyFormData);
  } catch (error) {
    console.warn(`Convermax /track failed: ${error instanceof Error ? error.message : String(error)}`);
  }
};

const getImpressionKey = (product: ImpressionPayload['Product']): string =>
  `${product.Id || ''}:${product.Position ?? ''}:${product.PageNumber ?? ''}:${product.PageSize ?? ''}`;

export function flushProductImpressions(): void {
  if (productImpressionTimer) {
    clearTimeout(productImpressionTimer);
    productImpressionTimer = null;
  }

  if (productImpressionQueue.length === 0) {
    return;
  }

  if (!requestConfig.isAnalyticsEnabled()) {
    productImpressionQueue.forEach(({ Product }) => productImpressionKeys.delete(getImpressionKey(Product)));
    productImpressionQueue.length = 0;
    return;
  }

  isFirstImpressionBatch = false;

  const batch = productImpressionQueue.splice(0, productImpressionQueue.length);
  const products = batch.map((payload) => JSON.stringify(payload.Product));

  baseTrack('ProductImpression', { Products: products });
}

const enqueueProductImpression = (payload: ImpressionPayload): void => {
  productImpressionQueue.push(payload);

  if (!productImpressionTimer) {
    const delay = isFirstImpressionBatch ? 0 : productImpressionBatchMs;
    productImpressionTimer = setTimeout(flushProductImpressions, delay);
  }
};

export default function track(type: TrackEventTypeName, eventParams: unknown): void {
  switch (type) {
    case 'ProductImpression':
      if (isProductImpressionEventParams(eventParams)) {
        trackProductImpression(eventParams);
      }
      break;
    case 'ClickOnRelatedProduct':
    case 'ClickOnSearchResult':
      if (isProductClickEventParams(eventParams)) {
        trackClickOnProductCard(eventParams);
      }
      break;
    case 'ProductView':
      if (isProductId(eventParams)) {
        trackSimpleProduct('ProductView', String(eventParams));
      }
      break;
    case 'AddToCart':
      if (isProductId(eventParams)) {
        trackSimpleProduct('AddToCart', String(eventParams));
      }
      break;
    default:
      if (isTelemetryAttributes(eventParams)) {
        baseTrack(type, eventParams);
      }
  }
}

function trackProductImpression({
  item,
  position,
  searchRequest,
}: {
  item: ProductTrackingItem;
  position: number;
  searchRequest?: SearchRequestPagination;
}): void {
  if (!item || !item.id) {
    return;
  }

  const currentSearchRequestKey = generateSearchId(searchRequest) || '';
  if (currentSearchRequestKey !== lastSearchRequestKey) {
    flushProductImpressions();

    productImpressionKeys.clear();
    lastSearchRequestKey = currentSearchRequestKey;
    isFirstImpressionBatch = true;
  }

  const product: ImpressionPayload['Product'] = {
    Id: String(item.id),
    Position: position,
    ...(searchRequest?.pageNumber !== undefined && { PageNumber: Number(searchRequest.pageNumber) }),
    ...(searchRequest?.pageSize !== undefined && { PageSize: Number(searchRequest.pageSize) }),
  };

  const impressionKey = getImpressionKey(product);
  if (productImpressionKeys.has(impressionKey)) {
    return;
  }

  productImpressionKeys.add(impressionKey);
  if (productImpressionKeys.size > maxImpressionKeys) {
    productImpressionKeys.clear();
  }

  enqueueProductImpression({
    Product: product,
  });
}

function trackClickOnProductCard({
  item,
  position,
  searchTotalHits,
}: {
  item: ProductTrackingItem;
  position: number;
  searchTotalHits?: number;
}): void {
  if (!item || !item.id) {
    return;
  }

  const eventParams = {
    Products: JSON.stringify({
      Id: String(item.id),
      Position: position,
    }),
  };

  if (item.type === 'related') {
    baseTrack('ClickOnRelatedProduct', eventParams);
  } else {
    baseTrack('ClickOnSearchResult', { ...eventParams, Total: searchTotalHits });
  }
}

function trackSimpleProduct(
  eventType: Extract<TrackEventTypeName, 'ProductView' | 'AddToCart'>,
  productId: string,
): void {
  if (!productId) {
    return;
  }

  baseTrack(eventType, {
    Products: JSON.stringify({
      Id: String(productId),
    }),
    ...(eventType === 'ProductView' && { Request: window.document.referrer || '' }),
  });
}

(function setupFlushOnPageLeave() {
  if (typeof window === 'undefined') {
    return;
  }

  window.addEventListener('pagehide', flushProductImpressions);

  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushProductImpressions();
    }
  });
})();
